import { propEvaluator } from './prop-evaluator.js'
import { hydrateWidget } from './hidrate-widget.js'
import { isBindable } from './is-bindable.js'

console.log('WIDGET DATA')

import Deferred, { DeferredPromise } from 'p-defer'

export const widgetDataSymbol = '__widget' //Symbol('widgetData')
export const widgetInitializedSymbol = '__widgetInitialized'

const camelToSnake = (name: string) =>
  // @ts-ignore
  name.replace(/[a-z][A-Z]/g, (m: string[]) => m[0] + '-' + m[1].toLowerCase())

export function buildFactory(spec: Record<string, any>) {
  const Alpine = window.Alpine
  // So that all widgets support variants
  spec.variant = 'default'

  const propDescriptorEntries = Object.entries(Object.getOwnPropertyDescriptors(spec))
  const bindablePropEntries: [string, any, string[], string[]][] = propDescriptorEntries
    .filter(([name, descriptor]) => !name.startsWith('_') && typeof descriptor.value !== 'function')
    .map(([name, spec]) => {
      const camelName = camelToSnake(name)
      const lowerName = name.toLowerCase()

      return [
        name,
        spec,
        [
          `:${camelName}`,
          `:${lowerName}`,
          `x-bind:${camelName}`,
          `x-bind:${lowerName}`,
          `x-prop:${camelName}`,
          `x-prop:${lowerName}`
        ],
        [camelName, lowerName]
      ]
    })

  let waitForRefs: string[] | null = null
  if (spec.init) {
    const usedRefs = propDescriptorEntries.reduce((refs, [, { value }]) => {
      if (typeof value !== 'function') return refs

      for (const match of value.toString().matchAll(/\bthis\.\$refs\.([a-z_][a-z0-9_]*)/gi)) {
        refs.add(match[1])
      }
      return refs
    }, new Set<string>())

    waitForRefs = usedRefs.size ? Array.from(usedRefs) : null
  }

  return function buildWidgetData(widgetEl: HTMLElement) {
    const initialized = Deferred<void>()

    const boundObjExpr = widgetEl.getAttribute('x-prop')
    const boundObj: Record<string, any> | null = boundObjExpr
      ? (window.Alpine.evaluate(widgetEl, boundObjExpr) as Record<string, any> | null)
      : null

    // Watch the attributes on the widget element
    const attrObserver = new MutationObserver((changes) => {
      changes.forEach(({ attributeName, target }) => {
        if (attributeName && target instanceof HTMLElement) {
          setProp(attributeName, target.getAttribute(attributeName))
        }
      })
    })

    attrObserver.observe(widgetEl, {
      attributes: true,
      attributeFilter: Object.keys(spec).map(camelToSnake),
      attributeOldValue: false
    })

    const widgetAttrs = widgetEl.attributes

    const boundProps: string[] = []
    for (let i = widgetAttrs.length - 1; i >= 0; i--) {
      const attr = widgetAttrs.item(i)!
      if (attr.name.startsWith('x-prop:')) {
        boundProps.push(attr.name.substring(7))
      }
    }

    const unboundProps = Object.fromEntries(
      propDescriptorEntries
        // remove all bounds props
        .filter(([propName]) => !boundProps.includes(propName))
        // map change the value to a clone of itself if it's an object
        .map((entry) =>
          entry[1].value && typeof entry[1].value === 'object'
            ? [
                entry[0],
                {
                  ...entry[1],
                  value: JSON.parse(JSON.stringify(entry[1].value))
                }
              ]
            : entry
        )
    )

    // Overwrite the init() method so that it waits for any $refs usage to resolve
    async function init() {
      Alpine.nextTick(() => hydrateWidget(widgetEl))

      // wait for all needed refs to be defined
      if (waitForRefs) {
        // @ts-ignore
        widgetEl._x_refs = await resolveRefs(widgetEl, waitForRefs)
      }

      if (spec.init) {
        await new Promise((resolve, reject) => {
          // invoke this way so that this is bound correctly
          Alpine.evaluate(widgetEl, async function () {
            try {
              // @ts-ignore since this will be bound at run time to the Alpine scope
              resolve(await spec.init.apply(this))
            } catch (err) {
              reject(err)
            }
          })
        })
      }

      initialized.resolve()
    }
    // promise that gets resolved when teh DOM has settled

    let settled: DeferredPromise<void>

    const data = Alpine.reactive(
      Object.assign(Object.create(Object.getPrototypeOf(spec), unboundProps), {
        init,
        [widgetDataSymbol]: true,
        [widgetInitializedSymbol]: initialized.promise,
        get $ready() {
          if (!settled) {
            settled = Deferred<void>()
            // @ts-ignore
            // Wait for init method in the widget to complete
            function settle() {
              observation.disconnect()
              settled.resolve()
            }

            let timerId = setTimeout(settle, 100)
            const observation = new MutationObserver((changes) => {
              // If a change occurs, kill the timer and start waiting another 100ms
              clearTimeout(timerId)
              timerId = setTimeout(settle, 100)
            })

            observation.observe(widgetEl, {
              attributes: true,
              attributeOldValue: true,
              subtree: true,
              childList: true
            })
          }
          return Promise.all([initialized.promise, settled.promise])
        },
        destroy() {
          attrObserver.disconnect()
        }
      })
    )

    const widgetElName = widgetEl.tagName
      .toLowerCase()
      .split('-')
      .map((part, index) => (index === 0 ? part : part[0].toUpperCase() + part.substring(1)))
      .join('')

    Object.defineProperty(data, '$' + widgetElName, {
      configurable: false,
      enumerable: false,
      get() {
        return Alpine.evaluate(widgetEl, '$data')
      }
    })

    Object.defineProperty(data, '$parent', {
      configurable: false,
      enumerable: false,
      get() {
        let targetEl = widgetEl.parentElement
        let result = null
        while (!result && targetEl) {
          const closestStack = Alpine.closestDataStack(targetEl) as (object & {
            [widgetDataSymbol]: any
          })[]
          result = closestStack.find((obj) => obj[widgetDataSymbol])
          targetEl = targetEl.parentElement
        }
        return result
      }
    })

    for (const [name, , bindMatches, attrMatches] of bindablePropEntries) {
      let propBinding
      for (let i = widgetAttrs.length - 1; i >= 0; i--) {
        const attr = widgetAttrs.item(i)!
        if (bindMatches.includes(attr.name)) {
          propBinding = attr
          break
        }
      }

      if (propBinding) {
        const expression = propBinding.value
        const read =
          // expression.match(/\$[a-z]+/)
          //   ? window.Alpine.evaluateLater(widgetEl, expression)
          propEvaluator(widgetEl, data, expression)
        const setter = isBindable(expression) ? propEvaluator(widgetEl, data, `${expression} = __`) : null

        let _value: any // used when not two-way binding expression to store any set value
        Object.defineProperty(data, name, {
          configurable: true,
          enumerable: true,
          get() {
            if (typeof _value !== 'undefined') {
              return _value
            } else {
              let result: any
              read((value: any) => (result = value))
              return result
            }
          },
          set(newValue) {
            if (setter) {
              setter(() => {}, { scope: { __: newValue } })
              return
            }
            // ensure that the value passed in is reactive if it's an array or an object
            _value = newValue && typeof newValue === 'object' ? Alpine.reactive(newValue) : newValue
          }
        })

        continue
      }

      let attr
      for (let i = widgetAttrs.length - 1; i >= 0; i--) {
        let a = widgetAttrs.item(i)!
        if (attrMatches.includes(a.name)) {
          attr = a
          break
        }
      }

      if (attr) {
        setProp(name, attr.value)
        continue
      }

      if (boundObj) {
        const boundVal = boundObj[name]
        const applyBound = typeof boundVal !== 'undefined'
        if (applyBound) {
          if (boundVal == '' || boundVal === null) {
            if (spec[name] !== '') {
              boundObj[name] = spec[name]
            }
          }

          // && typeof data[name] === 'undefined') {
          Object.defineProperty(data, name, {
            configurable: true, // so it can be deleted,
            enumerable: true, // so it appears on inspection
            get() {
              return boundObj[name]
            },
            set(newValue) {
              boundObj[name] = newValue
            }
          })
        }
      }
    }

    // validate attribValue to conform to spec
    function setProp(attributeName: string, attribValue: any) {
      const propName = bindablePropEntries.find(
        ([name]) => name.toLowerCase() === attributeName.replace(/-/g, '').toLowerCase()
      )?.[0]
      if (!propName) {
        console.log('cound not find prop for attribute', attributeName)
        return
      }

      const name = propName

      const defaultValue = spec[name]
      if (typeof defaultValue === 'boolean') {
        if (attribValue === '') {
          data[name] = true
        } else if (attribValue === 'false') {
          data[name] = false
        } else {
          data[name] = !!attribValue
        }
      } else if (typeof defaultValue === 'number') {
        data[name] = parseFloat(attribValue)
      } else if (typeof defaultValue === 'string') {
        data[name] = attribValue
      } else if (Array.isArray(defaultValue)) {
        data[name] = attribValue.trim().split(/\s*,\s*/g)
      } else if (defaultValue === null) {
        data[name] = attribValue
      } else {
        throw new Error('unsupported static attribute: ' + name)
      }
    }

    if (spec.destroy) {
      // @ts-ignore
      if (!widgetEl._x_cleanups) widgetEl._x_cleanups = []
      // @ts-ignore
      widgetEl._x_cleanups.push(() => {
        Alpine.evaluate(widgetEl, spec.destroy)
      })
    }

    return data
  }
}

const REFS_TIMEOUT = 10_000 // how long to wait for refs to exist before bailing.

async function resolveRefs(widgetEl: HTMLElement, refNames: string[]) {
  const refs = await new Promise<HTMLElement[]>((resolve, reject) => {
    let resolved = false

    setTimeout(() => {
      if (!resolved) {
        const missingRefs = refNames.filter((refName: string) => !widgetEl.querySelector(`[x-ref='${refName}']`))
        reject(new Error('timeout waiting for x-refs: ' + missingRefs.join(', ')))
      }
    }, REFS_TIMEOUT)
    function findRefs(): HTMLElement[] | null {
      const refs = refNames.map(
        (refName: string) =>
          // @ts-ignore
          widgetEl._x_refs?.[refName] ?? widgetEl.querySelector(`[x-ref='${refName}']`)
      )
      const allFound = refs.every((r) => !!r)
      if (allFound) {
        return refs
      }

      return null
    }

    const refs = findRefs()
    if (refs) {
      resolved = true
      resolve(refs)
      return
    }

    // Use a mutation observer to tell us when the content has changed so we can check for the refs again
    const domWatcher = new MutationObserver(() => {
      const refs = findRefs()
      if (refs) {
        resolved = true
        resolve(refs)
        domWatcher.disconnect()
      }
    })

    domWatcher.observe(widgetEl, {
      attributes: false,
      childList: true,
      subtree: true
    })
  })

  return Object.fromEntries(refNames.map((refName, index) => [refName, refs[index]]))
}

function findAttrib(attrs: NamedNodeMap, predicate: (attrName: string) => any) {
  let attr
  for (let i = attrs.length - 1; i >= 0; i--) {
    attr = attrs.item(i)!
    if (predicate(attr.name)) {
      return attr
    }
  }
  return null
}
